/**
 * DynamicField — renders a single custom field based on its `fieldtype`.
 *
 * Controlled component. Parent owns the value; this component never mutates
 * directly.
 */
import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';

const DynamicField = ({ field, value, onChange }) => {
  const { fieldname, label, fieldtype, required, options, help_text } = field;

  const handle = (next) => onChange(fieldname, next);
  const id = `custom-${fieldname}`;
  const testId = `custom-field-${fieldname}`;

  const renderControl = () => {
    switch (fieldtype) {
      case 'textarea':
        return (
          <Textarea
            id={id}
            value={value ?? ''}
            onChange={(e) => handle(e.target.value)}
            rows={3}
            data-testid={testId}
          />
        );
      case 'number':
        return (
          <Input
            id={id}
            type="number"
            value={value ?? ''}
            onChange={(e) => handle(e.target.value === '' ? null : Number(e.target.value))}
            data-testid={testId}
          />
        );
      case 'date':
        return (
          <Input
            id={id}
            type="date"
            value={value ?? ''}
            onChange={(e) => handle(e.target.value)}
            data-testid={testId}
          />
        );
      case 'boolean':
        return (
          <div className="flex items-center gap-2 pt-1">
            <Checkbox
              id={id}
              checked={!!value}
              onCheckedChange={(v) => handle(!!v)}
              data-testid={testId}
            />
            {help_text && <span className="text-xs text-gray-500">{help_text}</span>}
          </div>
        );
      case 'select':
        return (
          <Select
            value={value ?? ''}
            onValueChange={(v) => handle(v === '__none__' ? null : v)}
          >
            <SelectTrigger data-testid={testId}>
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              {!required && <SelectItem value="__none__">— None —</SelectItem>}
              {(options || []).map((opt) => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'text':
      default:
        return (
          <Input
            id={id}
            value={value ?? ''}
            onChange={(e) => handle(e.target.value)}
            data-testid={testId}
          />
        );
    }
  };

  return (
    <div className="space-y-1">
      <Label htmlFor={id} className="flex items-center gap-1">
        {label}
        {required && <span className="text-red-500" aria-label="required">*</span>}
      </Label>
      {renderControl()}
      {help_text && fieldtype !== 'boolean' && (
        <p className="text-xs text-gray-500">{help_text}</p>
      )}
    </div>
  );
};

export default DynamicField;
